
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import DataTable from "react-data-table-component";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* ICONS */
import { TbFileInvoice } from "react-icons/tb";
import { IoSyncOutline, IoCheckmarkCircleOutline } from "react-icons/io5";

/* SAMPLE DATA */
import inventory from "../../../../data/inventory";
import { useNavigate, useParams } from "react-router-dom";
import {
  getOrderDetailsAPI,
  syncOrderRazorpayAPI,
  markOrderPaidAPI,
} from "../../../../service/api/order.api";
import moment from "moment/moment";
import { IMAGE_URL, SET_FORMAT, FRONTEND_URL } from "../../../../config";
import { useSelector } from "react-redux";
import { handleAddToken } from "../../../../service/request";
import { toast } from "react-toast";

const OrderDetails = () => {
  /* GLOBAL STATES */
  const navigate = useNavigate();
  const { id } = useParams();
  const userDetails = useSelector((state) => state?.user);
  /* STATE VARIABLES */
  const [orderDetails, setOrderDetails] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [manualPaymentId, setManualPaymentId] = useState("");

  /* FUNCTIONS */
  const handleGetOrderDetailsFunction = async () => {
    try {
      await handleAddToken(userDetails?.token);
      const res = await getOrderDetailsAPI(id);
      if (res?.status) {
        setOrderDetails(res?.data);
      } else {
        console.log("Unexpected response status:", res?.status);
      }
    } catch (error) {
      console.log("Error fetching order details:", error);
    }
  };

  const handleSyncRazorpayFunction = async () => {
    try {
      setIsSyncing(true);
      await handleAddToken(userDetails?.token);
      const res = await syncOrderRazorpayAPI(id);
      if (res?.status && res?.data?.paymentStatus === "paid") {
        toast.success("Payment verified! Order marked as Paid.");
        handleGetOrderDetailsFunction();
      } else {
        toast.info(res?.message || "No captured payment found on Razorpay.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to sync with Razorpay.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleMarkPaidFunction = async () => {
    try {
      setIsMarkingPaid(true);
      await handleAddToken(userDetails?.token);
      const res = await markOrderPaidAPI(id, manualPaymentId);
      if (res?.status) {
        toast.success(res?.message || "Order marked as Paid successfully!");
        setShowMarkPaidModal(false);
        handleGetOrderDetailsFunction();
      } else {
        toast.error(res?.message || "Failed to mark order as paid.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Error updating order.");
    } finally {
      setIsMarkingPaid(false);
    }
  };

  

  /* ORDER COLUMNS */
  const columns = [
    {
      name: "Image",
      width: "15vh",
      selector: (row) => {
        const rawSrc =
          row?.image ||
          row?.product?.image?.front?.url ||
          row?.product?.image ||
          "";

        let primarySrc = "";
        let fallbackSrc = "";

        if (typeof rawSrc === "string" && rawSrc.startsWith("http")) {
          primarySrc = rawSrc;
        } else if (typeof rawSrc === "string" && rawSrc.startsWith("/")) {
          primarySrc = rawSrc;
          fallbackSrc = `${FRONTEND_URL || "https://www.thecida.com"}${rawSrc}`;
        } else if (rawSrc) {
          primarySrc = `${IMAGE_URL}${rawSrc}`;
          fallbackSrc = `${FRONTEND_URL || "https://www.thecida.com"}/${rawSrc}`;
        } else {
          primarySrc = "/logo/cida-emblem-white.png";
        }

        return (
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-neutral-900 border border-authBorder/20 flex items-center justify-center p-1 my-1">
            <img
              src={primarySrc}
              className="w-full h-full object-contain"
              alt={row?.name || row?.product?.name || "Product"}
              onError={(e) => {
                if (fallbackSrc && e.currentTarget.src !== fallbackSrc) {
                  e.currentTarget.src = fallbackSrc;
                } else if (!e.currentTarget.dataset.failed) {
                  e.currentTarget.dataset.failed = "true";
                  e.currentTarget.src = "/logo/cida-emblem-white.png";
                }
              }}
            />
          </div>
        );
      },
    },
    {
      name: "SKU / Size",
      width: "12vh",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.product?.sku || row?.sku || row?.size || "Standard"}
        </span>
      ),
    },
    {
      name: "HSN",
      width: "12vh",
      selector: (row) => {
        const rowName = row?.name || row?.product?.name || "";
        const isCandle =
          (rowName && rowName.toLowerCase().includes("candle")) ||
          row?.type === "candles" ||
          row?.product?.type === "candles";
        return (
          <span className="text-xs font-mono font-proxima-semibold text-black text-opacity-60">
            {row?.hsn ||
              row?.hsnCode ||
              row?.product?.hsn ||
              row?.product?.hsnCode ||
              (isCandle ? "340600" : "330300")}
          </span>
        );
      },
    },
    {
      name: "Product Name",
      width: "35vh",
      selector: (row) => (
        <span className="text-sm font-proxima-bold text-black">
          {row?.name || row?.product?.name || "CIDA Fragrance"}
        </span>
      ),
    },
    {
      name: "Price",
      width: "15vh",
      selector: (row) => (
        <span className="text-sm font-proxima-bold text-black">
          {SET_FORMAT(row?.amount)}
        </span>
      ),
    },
    {
      name: "Quantity",
      width: "15vh",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.quantity}
        </span>
      ),
    },
    {
      name: "Total Price",
      width: "15vh",
      selector: (row) => (
        <span className="text-sm font-proxima-bold text-black">
          {SET_FORMAT(row?.quantity * row?.amount)}
        </span>
      ),
    },
  ];

  /* USE-EFFECT */
  useEffect(() => {
    handleGetOrderDetailsFunction();
  }, [id]);
  return (
    <section className="px-0 py-2 xl:p-10 lg:p-10 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Order Details | CIDA Perfumes</title>
      </Helmet>

      <div className="container-full-width">
        <div className="block xl:flex lg:flex items-center justify-between">
          {/* HEADING */}
          <div>
            <h1 className="text-3xl font-proxima-bold text-black tracking-tight">
              Order Details
            </h1>
            <h2 className="text-lg font-proxima-regular text-greyText tracking-tight">
              Manage the order details, order confirmation and delivery details
              have been sent to{" "}
              <span className="font-proxima-bold">{orderDetails?.email}</span>
            </h2>
          </div>

          {/* VIEW INVOICE & ACTIONS */}
          <div className="flex flex-wrap items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            <Button
              onClick={() => {
                navigate("/orders");
              }}
              className="whitespace-nowrap shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 px-4 py-2.5 rounded-md border border-authBorder text-sm font-proxima-semibold text-black normal-case transition-all duration-300 ease-in-out"
            >
              Back to Orders
            </Button>
            <Button
              disabled={isSyncing}
              onClick={handleSyncRazorpayFunction}
              className="flex items-center gap-1.5 whitespace-nowrap justify-center shadow-none hover:shadow-none bg-neutral-900 hover:bg-black px-4 py-2.5 rounded-md border border-neutral-900 text-sm font-proxima-semibold text-white normal-case transition-all duration-300 ease-in-out"
            >
              <IoSyncOutline className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? "Syncing..." : "Sync with Razorpay"}
            </Button>
            {orderDetails?.paymentStatus !== "paid" && (
              <Button
                onClick={() => {
                  setManualPaymentId(
                    orderDetails?.paymentId || orderDetails?.razorpayPaymentId || ""
                  );
                  setShowMarkPaidModal(true);
                }}
                className="flex items-center gap-1.5 whitespace-nowrap justify-center shadow-none hover:shadow-none bg-green-700 hover:bg-green-800 px-4 py-2.5 rounded-md border border-green-700 text-sm font-proxima-semibold text-white normal-case transition-all duration-300 ease-in-out"
              >
                <IoCheckmarkCircleOutline className="w-4 h-4" /> Mark as Paid
              </Button>
            )}
            <Button
              onClick={() => {
                navigate(`/invoice/${id}`);
              }}
              className="whitespace-nowrap justify-center shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-4 py-2.5 rounded-md border border-logoYellow text-sm font-proxima-semibold text-white normal-case transition-all duration-300 ease-in-out flex items-center"
            >
              <TbFileInvoice className="w-4 h-4 mr-1" /> View Invoice
            </Button>
          </div>
        </div>

        {/* CUSTOMERS LIST */}
        <div className="mt-5 xl:mt-5 lg:mt-5">
          <div className=" bg-white border border-authBorder rounded-md overflow-y-auto overflow-x-hidden hide-scrollbar">
            <div className="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-4 h-full">
              <div className="col-span-3 h-full order-2 xl:order-1 lg:order-1">
                <div className="border-r border-authBorder h-full">
                  <div className="p-5 xl:p-5 lg:p-5">
                    <div className="grid grid-cols-12 h-full gap-5 pb-3 ">
                      {/* SHIPPING INFORMATION */}
                      <div className="col-span-12 lg:col-span-6 px-3 py-3 ">
                        <h2 className="text-xl font-proxima-bold text-black">
                          Shipping details
                        </h2>
                        <h3 className="text-base font-proxima-regular text-greyText border-b border-authBorder pb-2">
                          Shipping details include recipient information
                          necessary for smooth and efficient order delivery.
                        </h3>

                        <p className="text-base font-proxima-regular text-greyText mt-3">
                          {orderDetails?.personalDetails?.firstName}{" "}
                          {orderDetails?.personalDetails?.lastName}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText">
                          {orderDetails?.personalDetails?.email}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText">
                          {orderDetails?.personalDetails?.phoneNumber}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText mt-2">
                          {orderDetails?.shippingDetails?.address}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText ">
                          {orderDetails?.shippingDetails?.city},{" "}
                          {orderDetails?.shippingDetails?.state},{" "}
                          {orderDetails?.shippingDetails?.zipCode},{" "}
                          {orderDetails?.shippingDetails?.country}
                        </p>

                        {/* <h3 className="text-xl font-proxima-bold text-black border-b border-authBorder pb-2 mt-5">
                          Order information
                        </h3>

                        <p className="text-base font-proxima-regular text-greyText mt-2">
                          <span className="text-black font-proxima-bold">
                            Shipping Type :
                          </span>{" "}
                          Ground Advantage - Package{" "}
                          <span className="text-black font-proxima-bold">
                            ( ₹80.00 )
                          </span>
                        </p>
                        <p className="text-lg font-proxima-regular text-[#FFB129]  mt-2">
                          <span className="text-base text-black font-proxima-bold">
                            Order Status :
                          </span>{" "}
                          Payment Received & Order Placed
                        </p> */}
                      </div>

                      {/* BILLING INFORMATION */}
                      <div className="col-span-12 lg:col-span-6 px-3 py-3">
                        <h2 className="text-xl font-proxima-bold text-black">
                          Billing details
                        </h2>
                        <h3 className="text-base font-proxima-regular text-greyText border-b border-authBorder pb-2">
                          Billing details, including payment method and
                          transaction specifics, ensure accurate and complete
                          order processing.
                        </h3>

                        <p className="text-base font-proxima-regular text-greyText mt-3">
                          {orderDetails?.personalDetails?.firstName}{" "}
                          {orderDetails?.personalDetails?.lastName}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText">
                          {orderDetails?.personalDetails?.email}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText">
                          {orderDetails?.personalDetails?.phoneNumber}
                        </p>

                        <h3 className="text-xl font-proxima-bold text-black border-b border-authBorder pb-2 mt-5">
                          Payment information
                        </h3>

                        <div className="mt-3 space-y-2">
                          <p className="text-sm font-proxima-regular text-greyText">
                            <span className="text-black font-proxima-bold">
                              Payment Mode:
                            </span>{" "}
                            {orderDetails?.paymentMode || "Razorpay"}
                          </p>
                          <p className="text-sm font-proxima-regular text-greyText">
                            <span className="text-black font-proxima-bold">
                              Razorpay Order ID:
                            </span>{" "}
                            <span className="font-mono text-neutral-800">
                              {orderDetails?.razorpayOrderId || "Not Generated"}
                            </span>
                          </p>
                          <p className="text-sm font-proxima-regular text-greyText">
                            <span className="text-black font-proxima-bold">
                              Razorpay Payment ID:
                            </span>{" "}
                            <span className="font-mono text-neutral-800">
                              {orderDetails?.paymentId || orderDetails?.razorpayPaymentId || "None"}
                            </span>
                          </p>
                          <div className="flex items-center gap-2 pt-1">
                            <span className="text-black font-proxima-bold text-sm">
                              Payment Status:
                            </span>
                            {orderDetails?.paymentStatus === "paid" ? (
                              <span className="inline-flex items-center text-xs font-proxima-bold px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200">
                                Paid
                              </span>
                            ) : orderDetails?.paymentStatus === "pending" ? (
                              <span className="inline-flex items-center text-xs font-proxima-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                Pending
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-xs font-proxima-bold px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200">
                                Failed
                              </span>
                            )}
                          </div>
                          {orderDetails?.shipRocketId && (
                            <p className="text-sm font-proxima-regular text-greyText pt-1">
                              <span className="text-black font-proxima-bold">
                                Shiprocket Tracking ID:
                              </span>{" "}
                              <span className="font-mono text-neutral-800">
                                {orderDetails?.shipRocketId}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* PRODUCTS */}
                    <div className="grid grid-cols-12 h-full gap-5 pt-3 border-t border-authBorder ">
                      {/* SHIPPING INFORMATION */}
                      <div className="col-span-12 px-3 py-3">
                        <h2 className="text-xl font-proxima-bold text-black">
                          Products ({orderDetails?.order?.length})
                        </h2>
                        {/* <h3 className="text-base font-proxima-regular text-greyText  pb-2">
                                                Shipping details include recipient information necessary for smooth and efficient order delivery.
                                            </h3> */}

                        {/* ORDERS TABLE */}
                        <div className="mt-3">
                          <DataTable
                            className="data-table"
                            data={orderDetails?.order}
                            columns={columns}
                            responsive
                            pagination={false}
                            paginationTotalRows={15}
                            noDataComponent={
                              <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
                                <p className="text-sm font-proxima-semibold text-black text-center">
                                  There are no Products to display.
                                </p>
                              </div>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-1 h-full order-1 xl:order-2 lg:order-2">
                <div className="p-5 xl:p-5 lg:p-5">
                  <div className=" border-b border-authBorder px-0 py-5 xl:px-2 lg:px-2 ">
                    <h2 className="text-xl font-proxima-bold text-black">
                      Order ID #{orderDetails?.orderId}
                    </h2>
                    <h3 className=" text-sm font-proxima-regular text-greyText  pb-2">
                      {moment(orderDetails?.createdAt).format(
                        "DD MMMM YYYY, hh:mm A"
                      )}
                    </h3>
                    <div className="mt-3">
                      {orderDetails?.paymentStatus === "paid" || orderDetails?.orderStatus === "Order Placed" ? (
                        <span className="inline-flex items-center text-xs font-proxima-bold px-3 py-1 rounded-full bg-green-100 text-green-800 border border-green-200">
                          {orderDetails?.orderStatus || "Order Placed"}
                        </span>
                      ) : orderDetails?.paymentStatus === "pending" ? (
                        <span className="inline-flex items-center text-xs font-proxima-bold px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          {orderDetails?.orderStatus || "Payment Pending"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-proxima-bold px-3 py-1 rounded-full bg-red-100 text-red-800 border border-red-200">
                          {orderDetails?.orderStatus || "Payment Failed"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="bg-greyBg rounded-md w-full py-2 px-5">
                      <p className="text-sm font-proxima-semibold text-greyText">
                        Summary
                      </p>
                    </div>

                    {/* SUBTOTAL */}
                    <div className="grid grid-cols-2 border-b border-authBorder px-5 py-5 xl:p-5 lg:p-5">
                      <div className="col-span-1">
                        <p className="text-base font-proxima-regular text-greyText">
                          Subtotal
                        </p>
                      </div>
                      <div className="col-span-1">
                        <p className="text-base font-proxima-bold text-black float-right">
                          {SET_FORMAT(orderDetails?.subTotal || orderDetails?.amount)}
                        </p>
                      </div>
                    </div>

                    {/* DISCOUNT */}
                    <div className="grid grid-cols-2 border-b border-authBorder px-5 py-5 xl:p-5 lg:p-5">
                      <div className="col-span-1">
                        <p className="text-base font-proxima-regular text-greyText">
                          Discount {orderDetails?.couponCode ? `(${orderDetails.couponCode})` : ""}
                        </p>
                      </div>
                      <div className="col-span-1">
                        <p className="text-base font-proxima-bold text-black float-right">
                          {SET_FORMAT(orderDetails?.discountAmount)}
                        </p>
                      </div>
                    </div>

                    {/* SHIPPING COST */}
                    <div className="grid grid-cols-2 border-b border-authBorder px-5 py-5 xl:p-5 lg:p-5">
                      <div className="col-span-1">
                        <p className="text-base font-proxima-regular text-greyText">
                          Shipment cost
                        </p>
                      </div>
                      <div className="col-span-1">
                        <p className="text-base font-proxima-bold text-black float-right">
                          {SET_FORMAT(orderDetails?.shippingCharge)}
                        </p>
                      </div>
                    </div>

                    {/* GRAND TOTAL */}
                    <div className="grid grid-cols-2  px-5 py-5 xl:p-5 lg:p-5">
                      <div className="col-span-1">
                        <p className="text-lg font-proxima-bold text-black">
                          Grand Total
                        </p>
                      </div>
                      <div className="col-span-1">
                        <p className="text-lg font-proxima-bold text-black float-right">
                          {SET_FORMAT(orderDetails?.payAmount || orderDetails?.amount)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="bg-greyBg rounded-md w-full py-2 px-5">
                      <p className="text-sm font-proxima-semibold text-greyText">
                        Notes
                      </p>
                    </div>
                    <p className="text-base font-proxima-regular p-3 text-greyText">
                      {orderDetails?.note ? orderDetails?.note : "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MANUAL MARK AS PAID MODAL */}
      {showMarkPaidModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
            <h3 className="text-xl font-proxima-bold text-black mb-1">
              Mark Order as Paid
            </h3>
            <p className="text-xs font-proxima-regular text-greyText mb-4 leading-relaxed">
              Enter or confirm the Razorpay Payment ID. This will update the order to <strong className="text-black">PAID</strong>, trigger Shiprocket fulfillment, and dispatch Brevo confirmation emails to the customer.
            </p>
            <div className="mb-4">
              <label className="block text-xs font-proxima-bold text-black uppercase mb-1">
                Razorpay Payment / Transaction ID
              </label>
              <input
                type="text"
                value={manualPaymentId}
                onChange={(e) => setManualPaymentId(e.target.value)}
                placeholder="e.g. pay_TgZK6tVafQ5OMk"
                className="w-full px-3 py-2 border border-authBorder rounded-md text-sm font-mono text-black focus:outline-none focus:border-black"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                onClick={() => setShowMarkPaidModal(false)}
                className="bg-neutral-100 hover:bg-neutral-200 text-black shadow-none px-4 py-2 text-sm font-proxima-semibold normal-case rounded-md"
              >
                Cancel
              </Button>
              <Button
                disabled={isMarkingPaid}
                onClick={handleMarkPaidFunction}
                className="bg-green-700 hover:bg-green-800 text-white shadow-none px-4 py-2 text-sm font-proxima-semibold normal-case rounded-md flex items-center gap-1"
              >
                {isMarkingPaid ? "Updating..." : "Confirm & Mark Paid"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
export default OrderDetails;
